import { AnyExtension, ChainedFromExtensions } from "remirror";
import { trackEvent } from "../../features/analytics/analytics";
import {
  SNIPPET_VARIANTS_LIST,
  getSnippetLines,
  getSnippetText,
  isHtmlSnippet,
} from "../../features/design/expression-snippets";

export const EDITOR_MODE_KEY = "cm_html_editor_mode";
export const EDITOR_MODE_EVENT = "cm_editor_mode_change";
export type EditorMode = "wysiwyg" | "html";

export function getStoredEditorMode(): EditorMode {
  try {
    const stored = localStorage.getItem(EDITOR_MODE_KEY);
    if (stored === "html" || stored === "wysiwyg") return stored;
  } catch {
    // ignore
  }
  return "wysiwyg";
}

export function setStoredEditorMode(mode: EditorMode) {
  try {
    localStorage.setItem(EDITOR_MODE_KEY, mode);
  } catch {
    // ignore
  }
}

const SELF_CLOSING_TAGS = new Set([
  "br","hr","img","input","meta","link","area","base","col","embed","source","track","wbr",
]);

export function validateHtml(content: string): string | null {
  if (!content?.trim()) return null;
  const openTags: string[] = [];
  const tagRe = /<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^>]*\/?>/g;
  let m: RegExpExecArray | null;
  while ((m = tagRe.exec(content)) !== null) {
    const tag = m[1].toLowerCase();
    if (SELF_CLOSING_TAGS.has(tag) || m[0].endsWith("/>")) continue;
    if (m[0].startsWith("</")) {
      const last = openTags.pop();
      if (last && last !== tag) {
        return `Possible unclosed tag: <${last}> (found </${tag}> instead)`;
      }
    } else {
      openTags.push(tag);
    }
  }
  if (openTags.length > 0) {
    return `Possible unclosed tag(s): ${openTags.map((t) => `<${t}>`).join(", ")}`;
  }
  const brokenExpr = content.match(/\{\{[^}]*$/m);
  if (brokenExpr) {
    return "Possible unclosed expression: missing }}";
  }
  return null;
}

export interface HtmlEditorRef {
  setContent: (content: string) => void;
  replaceContent: (content: string) => void;
  focus: () => void;
  insertText: (content: string) => void;
  insertParagraph: (content: string) => void;
  chain: ChainedFromExtensions<AnyExtension>;
}

export const insertFieldToEditor = (
  content: string,
  args,
  { chain, insertText }: Partial<HtmlEditorRef>,
  /**
   * Handles HTML snippets (the variants table). They contain the
   * comment-wrapped `<!--{{Variants}}-->` iterator, so they are never pushed
   * through the WYSIWYG editor — the caller routes them to the HTML source view.
   */
  onHtmlSnippet?: (html: string) => void
) => {
  if (chain) {
    trackEvent({
      category: "designer",
      action: "insert-expression",
      label: content,
      metadata: { source: "toolbar" },
      internal: true,
    });

    const category = args?.product?.category;

    if (isHtmlSnippet(content)) {
      onHtmlSnippet?.(getSnippetText(content, category));
      return;
    }

    const lines = getSnippetLines(content, category);
    if (lines) {
      // Paragraphs are inserted last-first so they end up in source order.
      // The closing line of the variants iterator keeps its historical
      // `selection: "end"` — the other snippets never had it.
      const [first, ...rest] = lines;
      let c = chain.emptySelection();
      rest
        .slice()
        .reverse()
        .forEach((line, i) => {
          const isClosingLine = i === 0;
          c =
            isClosingLine && content === SNIPPET_VARIANTS_LIST
              ? c.insertParagraph(line, { selection: "end" })
              : c.insertParagraph(line);
        });
      c.insertParagraph(first).focus().run();
    } else {
      insertText(content);
    }
  }
};
